import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowRight, Search, Car, Phone, Star, CheckCircle, 
  XCircle, Clock, MoreVertical, MapPin, DollarSign
} from 'lucide-react';
import { driverService } from '@/services/driverService';
import type { Driver } from '@/types';

const OwnerDrivers: React.FC = () => {
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [filteredDrivers, setFilteredDrivers] = useState<Driver[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'pending' | 'online'>('all');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadDrivers();
  }, []);

  useEffect(() => {
    let filtered = drivers;
    
    if (searchTerm) {
      filtered = filtered.filter(d => 
        d.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.phone.includes(searchTerm) ||
        d.vehicle?.plateNumber?.includes(searchTerm)
      );
    }
    
    if (activeTab === 'pending') {
      filtered = filtered.filter(d => !d.isVerified);
    } else if (activeTab === 'online') {
      filtered = filtered.filter(d => d.isOnline);
    }
    
    setFilteredDrivers(filtered);
  }, [searchTerm, activeTab, drivers]);

  const loadDrivers = async () => {
    try {
      const data = await driverService.getAllDrivers(100);
      setDrivers(data);
      setFilteredDrivers(data);
    } catch (error) {
      console.error('Error loading drivers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (driverId: string) => {
    try {
      await driverService.verifyDriver(driverId);
      loadDrivers();
    } catch (error) {
      console.error('Error verifying driver:', error);
    }
  };

  const handleDeactivate = async (driverId: string) => {
    try {
      await driverService.deactivateDriver(driverId);
      loadDrivers();
    } catch (error) {
      console.error('Error deactivating driver:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="flex items-center gap-4 p-4">
          <button
            onClick={() => navigate('/owner/dashboard')}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <ArrowRight className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-bold">السائقين</h1>
        </div>
      </header>

      <main className="p-6">
        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="البحث بالاسم أو الهاتف أو اللوحة..."
            className="w-full bg-white border border-gray-200 rounded-xl py-3 pr-12 pl-4 focus:outline-none focus:border-primary-500 transition-colors"
          />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          {[
            { key: 'all', label: 'الكل', count: drivers.length },
            { key: 'pending', label: 'بانتظار التحقق', count: drivers.filter(d => !d.isVerified).length },
            { key: 'online', label: 'متصل الآن', count: drivers.filter(d => d.isOnline).length },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as any)}
              className={`px-4 py-2 rounded-xl font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.key
                  ? 'bg-primary-500 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {tab.label}
              <span className={`mr-2 px-2 py-0.5 rounded-full text-xs ${
                activeTab === tab.key ? 'bg-white/20' : 'bg-gray-100'
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Drivers List */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredDrivers.length === 0 ? (
          <div className="text-center py-12">
            <Car className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">لا يوجد سائقين</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredDrivers.map((driver) => (
              <div key={driver.id} className="bg-white rounded-2xl p-4 shadow-sm">
                <div className="flex items-start gap-4">
                  {/* Photo */}
                  <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                    {driver.photoURL ? (
                      <img src={driver.photoURL} alt={driver.name} className="w-14 h-14 rounded-full" />
                    ) : (
                      <Car className="w-7 h-7 text-gray-400" />
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-bold text-gray-800">{driver.name}</h3>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Phone className="w-4 h-4" />
                            {driver.phone}
                          </span>
                          <span className="flex items-center gap-1">
                            <Star className="w-4 h-4 text-yellow-500" />
                            {driver.rating || '5.0'}
                          </span>
                        </div>
                      </div>
                      
                      {/* Status Badge */}
                      <div className="flex gap-2">
                        {!driver.isVerified && (
                          <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
                            بانتظار التحقق
                          </span>
                        )}
                        {driver.isOnline && (
                          <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                            متصل
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Vehicle Info */}
                    <div className="mt-3 p-3 bg-gray-50 rounded-xl">
                      <div className="flex items-center gap-4 text-sm">
                        <span className="flex items-center gap-1 text-gray-600">
                          <Car className="w-4 h-4" />
                          {driver.vehicle?.make} {driver.vehicle?.model}
                        </span>
                        <span className="flex items-center gap-1 text-gray-600">
                          <MapPin className="w-4 h-4" />
                          {driver.vehicle?.plateNumber}
                        </span>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="flex gap-6 mt-3 text-sm">
                      <span className="text-gray-500">
                        الرحلات: <strong className="text-gray-800">{driver.totalRides || 0}</strong>
                      </span>
                      <span className="text-gray-500">
                        الأرباح: <strong className="text-gray-800">{driver.earnings?.total?.toLocaleString() || 0} ج.س</strong>
                      </span>
                      <span className="text-gray-500">
                        نسبة القبول: <strong className="text-gray-800">{driver.acceptanceRate || 100}%</strong>
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 mt-4">
                      {!driver.isVerified && (
                        <button
                          onClick={() => handleVerify(driver.id)}
                          className="flex-1 bg-green-500 text-white py-2 rounded-xl text-sm font-medium hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
                        >
                          <CheckCircle className="w-4 h-4" />
                          تحقق
                        </button>
                      )}
                      <button
                        onClick={() => handleDeactivate(driver.id)}
                        className="flex-1 bg-red-500 text-white py-2 rounded-xl text-sm font-medium hover:bg-red-600 transition-colors flex items-center justify-center gap-2"
                      >
                        <XCircle className="w-4 h-4" />
                        تعطيل
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default OwnerDrivers;
